export type WatchdogState = 'INIT' | 'CONNECTING' | 'CONNECTED' | 'DISCONNECTED';

export interface WatchdogTransitionMeta {
  reason?: string;
  detail?: string;
  [key: string]: unknown;
}

export type WatchdogListener = (
  nextState: WatchdogState,
  previousState: WatchdogState,
  meta?: WatchdogTransitionMeta,
) => void;

export interface WatchdogEvent {
  id: string;
  timestamp: number;
  type: 'transition' | 'heartbeat' | 'activity' | 'socket' | 'error';
  message: string;
  state: WatchdogState;
  meta?: WatchdogTransitionMeta;
}

export interface WatchdogMetrics {
  state: WatchdogState;
  lastActivityAt: number;
  eventCount: number;
  heartbeatCount: number;
  errorCount: number;
  lastTransitionAt: number;
}

export interface ConnectionWatchdog {
  start: () => WatchdogState;
  stop: () => WatchdogState;
  connected: (meta?: WatchdogTransitionMeta) => WatchdogState;
  disconnected: (meta?: WatchdogTransitionMeta) => WatchdogState;
  error: (meta?: WatchdogTransitionMeta) => WatchdogState;
  state: () => WatchdogState;
  getState: () => WatchdogState;
  markActivity: (meta?: WatchdogTransitionMeta) => void;
  recordEvent: (event: Omit<WatchdogEvent, 'id' | 'timestamp' | 'state'>) => WatchdogEvent;
  getEvents: () => WatchdogEvent[];
  getMetrics: () => WatchdogMetrics;
  getSnapshot: () => {
    state: WatchdogState;
    events: WatchdogEvent[];
    metrics: WatchdogMetrics;
  };
  clear: () => void;
  subscribe: (listener: WatchdogListener) => () => void;
}

interface ConnectionWatchdogOptions {
  label?: string;
  debug?: boolean;
  heartbeatIntervalMs?: number;
  inactivityTimeoutMs?: number;
}

export function createConnectionWatchdog(
  options: ConnectionWatchdogOptions = {},
): ConnectionWatchdog {
  const label = options.label ?? 'watchdog';
  const debugEnabled = options.debug ?? true;
  const heartbeatIntervalMs = options.heartbeatIntervalMs ?? 15000;
  const inactivityTimeoutMs = options.inactivityTimeoutMs ?? 90000;
  const listeners = new Set<WatchdogListener>();
  const eventHistory: WatchdogEvent[] = [];
  const maxEvents = 50;
  let state: WatchdogState = 'INIT';
  let lastActivityAt = Date.now();
  let lastTransitionAt = Date.now();
  let heartbeatTimer: ReturnType<typeof globalThis.setInterval> | null = null;
  let heartbeatCount = 0;
  let errorCount = 0;

  const log = (message: string, meta?: WatchdogTransitionMeta) => {
    if (!debugEnabled) {
      return;
    }

    console.debug(`[watchdog:${label}] ${message}`, meta ?? {});
  };

  const pushEvent = (event: Omit<WatchdogEvent, 'id' | 'timestamp' | 'state'>) => {
    const fullEvent: WatchdogEvent = {
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      timestamp: Date.now(),
      state,
      ...event,
    };

    eventHistory.push(fullEvent);
    if (eventHistory.length > maxEvents) {
      eventHistory.splice(0, eventHistory.length - maxEvents);
    }

    return fullEvent;
  };

  const transition = (nextState: WatchdogState, meta?: WatchdogTransitionMeta) => {
    if (state === nextState) {
      log(`state unchanged -> ${nextState}`, meta);
      pushEvent({
        type: 'transition',
        message: `state unchanged -> ${nextState}`,
        meta,
      });
      return state;
    }

    const previousState = state;
    state = nextState;
    lastTransitionAt = Date.now();
    log(`state transition ${previousState} -> ${nextState}`, meta);
    pushEvent({
      type: 'transition',
      message: `state transition ${previousState} -> ${nextState}`,
      meta,
    });

    for (const listener of Array.from(listeners)) {
      listener(state, previousState, meta);
    }

    return state;
  };

  const clearHeartbeat = () => {
    if (heartbeatTimer) {
      globalThis.clearInterval(heartbeatTimer);
      heartbeatTimer = null;
    }
  };

  const startHeartbeat = () => {
    clearHeartbeat();

    heartbeatTimer = globalThis.setInterval(() => {
      if (state !== 'CONNECTED') {
        return;
      }

      heartbeatCount += 1;
      pushEvent({
        type: 'heartbeat',
        message: 'heartbeat tick',
        meta: { lastActivityAt, ageMs: Date.now() - lastActivityAt },
      });

      if (Date.now() - lastActivityAt > inactivityTimeoutMs) {
        log('heartbeat timeout reached, connection considered stalled', { reason: 'heartbeat-timeout' });
        errorCount += 1;
        pushEvent({
          type: 'error',
          message: 'heartbeat timeout reached',
          meta: { reason: 'heartbeat-timeout' },
        });
        transition('DISCONNECTED', { reason: 'heartbeat-timeout', detail: 'No activity observed in time' });
      }
    }, heartbeatIntervalMs);
  };

  return {
    start: () => {
      if (state === 'CONNECTING' || state === 'CONNECTED') {
        log('start ignored because the watchdog is already active', { reason: 'start' });
        return state;
      }

      lastActivityAt = Date.now();
      const nextState = transition('CONNECTING', { reason: 'start' });
      startHeartbeat();
      return nextState;
    },

    stop: () => {
      clearHeartbeat();
      return transition('DISCONNECTED', { reason: 'stop' });
    },

    connected: (meta) => {
      lastActivityAt = Date.now();
      return transition('CONNECTED', { reason: meta?.reason ?? 'connected', ...meta });
    },

    disconnected: (meta) => {
      clearHeartbeat();
      return transition('DISCONNECTED', { reason: meta?.reason ?? 'disconnected', ...meta });
    },

    error: (meta) => {
      clearHeartbeat();
      return transition('DISCONNECTED', { reason: meta?.reason ?? 'error', ...meta });
    },

    state: () => state,
    getState: () => state,
    markActivity: (meta) => {
      lastActivityAt = Date.now();
      pushEvent({
        type: 'activity',
        message: 'activity observed',
        meta,
      });
    },
    recordEvent: (event) => {
      if (event.type === 'error') {
        errorCount += 1;
      }
      return pushEvent(event);
    },
    getEvents: () => [...eventHistory],
    getMetrics: () => ({
      state,
      lastActivityAt,
      eventCount: eventHistory.length,
      heartbeatCount,
      errorCount,
      lastTransitionAt,
    }),
    getSnapshot: () => ({
      state,
      events: [...eventHistory],
      metrics: {
        state,
        lastActivityAt,
        eventCount: eventHistory.length,
        heartbeatCount,
        errorCount,
        lastTransitionAt,
      },
    }),
    clear: () => {
      eventHistory.length = 0;
      heartbeatCount = 0;
      errorCount = 0;
      lastActivityAt = Date.now();
      lastTransitionAt = Date.now();
    },
    subscribe: (listener) => {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
  };
}
